// Simplified TS port of the "Finish" tab sentence generation in
// TOPS-WEB-NZ's InternalDescriptionPopupApp.js (CreateSentences / CreateSentenceAdditionalFeatures / CreateSentencePCItems).
// Covers Accommodation Plus, Chattels, Fixtures and Features, Outdoor Area, and the per-room
// "PC Items" paragraph used as the Finish tab's Accommodation Description.
import { INTERNAL_DESCRIPTION_CATEGORIES } from './internal-description-categories';
import { INTERNAL_DESCRIPTION_OPTIONS, InternalDescriptionOption } from './internal-description-options';

export interface QuickChoiceSentences {
  accommodationPlus: string;
  chattels: string;
  fixturesAndFeatures: string;
  outdoorArea: string;
}

function categoryHasValue(selections: Record<string, string>, key: string): boolean {
  const category = INTERNAL_DESCRIPTION_CATEGORIES.find((item) => item.key === key);
  return !!category && category.sections.some((section) => !!selections[section.fieldGroup]);
}

function containsOption(value: string | undefined, needle: string): boolean {
  return !!value && value.toLowerCase().includes(needle.toLowerCase());
}

function sentenceCase(value: string): string {
  const trimmed = value.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : trimmed;
}

function trimTrailingComma(value: string): string {
  return value.replace(/,\s*$/, '');
}

function replaceLastCommaWithAnd(value: string): string {
  const lastComma = value.lastIndexOf(',');
  if (lastComma === -1) return value;
  return `${value.slice(0, lastComma)} and${value.slice(lastComma + 1)}`;
}

function roomWithBar(selections: Record<string, string>, categoryKey: string, fixturesField: string, label: string): string {
  if (!categoryHasValue(selections, categoryKey)) return '';
  const fixtures = selections[fixturesField];
  if (containsOption(fixtures, 'wet bar')) return `${label} with wet bar, `;
  if (containsOption(fixtures, 'bar')) return `${label} with bar, `;
  return `${label}, `;
}

export function buildQuickChoiceSentences(selections: Record<string, string>): QuickChoiceSentences {
  let accPlus = '';

  if (categoryHasValue(selections, 'Entry')) accPlus += 'entry, ';
  accPlus += roomWithBar(selections, 'Lounge', 'LoungeFixtures', 'lounge');
  accPlus += roomWithBar(selections, 'Dining', 'DiningFixture', 'dining');
  if (categoryHasValue(selections, 'Kitchen')) accPlus += 'kitchen, ';
  if (categoryHasValue(selections, 'Kitchen2')) accPlus += 'kitchen 2, ';
  accPlus += roomWithBar(selections, 'Family', 'FamilyFixture', 'family');
  accPlus += roomWithBar(selections, 'Meals', 'MealsFixture', 'meals');
  accPlus += roomWithBar(selections, 'Rumpus', 'RumpusFixture', 'rumpus');
  accPlus += roomWithBar(selections, 'Media', 'MediaFixture', 'media');

  if (categoryHasValue(selections, 'Laundry')) {
    const location = selections['LaundryLocation'];
    accPlus += location && location.toLowerCase() !== 'standard' ? `laundry (${location.toLowerCase()}), ` : 'laundry, ';
  }

  const separateToilets = Number(selections['Count'] || 0);
  if (separateToilets > 0) accPlus += `${separateToilets} separate toilet${separateToilets > 1 ? 's' : ''}, `;

  if (categoryHasValue(selections, 'PowderRoom')) accPlus += 'powder room, ';
  if (categoryHasValue(selections, 'Study')) accPlus += 'study, ';
  if (selections['Other']) accPlus += `${selections['Other'].toLowerCase()}, `;
  if (selections['OutdoorArea']) accPlus += `${selections['OutdoorArea'].toLowerCase()}, `;
  if (selections['MoreRooms']) accPlus += `${selections['MoreRooms'].toLowerCase()}, `;

  accPlus = trimTrailingComma(accPlus.trim());
  const accommodationPlus = accPlus ? `${sentenceCase(replaceLastCommaWithAnd(accPlus))}.` : '';

  let chattels = '';
  if (categoryHasValue(selections, 'Chattels')) chattels = 'The dwelling is fitted with ';
  if (selections['ChattelsQuality']) chattels += selections['ChattelsQuality'].toLowerCase();
  if (selections['ChattelsCondition']) chattels += ` quality chattels presented in ${selections['ChattelsCondition'].toLowerCase()} condition`;
  if (selections['ChattelsSelection']) chattels += `. These include ${trimTrailingComma(selections['ChattelsSelection'].toLowerCase())}.`;

  const fixturesAndFeatures = selections['InternalFixtures']
    ? `${sentenceCase(trimTrailingComma(selections['InternalFixtures'].toLowerCase()))}.`
    : '';

  const outdoorArea = selections['OutdoorArea'] ? `${sentenceCase(trimTrailingComma(selections['OutdoorArea'].toLowerCase()))}.` : '';

  return { accommodationPlus, chattels, fixturesAndFeatures, outdoorArea };
}

function removeLastComma(value: string): string {
  const trimmed = value.trim();
  return trimmed.endsWith(',') ? trimmed.slice(0, -1) : trimmed;
}

const UNITS_TEXT = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];

/** Mirrors legacy unitsText: spells out small counts (used for "Separate Toilets"). */
function unitsText(value: number): string {
  return UNITS_TEXT[value] || '';
}

/** Mirrors legacy ReplaceLastComa: only normalizes whitespace around the final comma (no "and" substitution). */
function normalizeLastComma(value: string): string {
  if (value.length < 3) return value;
  const lastComma = value.lastIndexOf(',');
  if (lastComma < 0) return value;
  const before = value.slice(0, lastComma).replace(/\s+$/, '');
  const after = value.slice(lastComma + 1).replace(/^\s+/, '');
  return `${before}, ${after}`;
}

/** Mirrors legacy sentenceCase: lowercase, then capitalize the first letter of each sentence. */
function toSentenceCase(value: string): string {
  return value.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (match) => match.toUpperCase());
}

function combineFixturesAndFloor(selections: Record<string, string>, fixturesField: string, floorField: string): string {
  let tmp = '';
  const fixtures = selections[fixturesField];
  if (fixtures) tmp += removeLastComma(fixtures);
  const floor = selections[floorField];
  if (floor) tmp += tmp.trim() !== '' ? `,${floor}` : floor;
  return tmp;
}

function roomLine(label: string, tmp: string): string {
  return `${label}: ${toSentenceCase(normalizeLastComma(tmp))}`;
}

/** Ordered simple rooms sharing the "floor coverings + fixtures" section layout. */
const SIMPLE_ROOM_FIELDS: { categoryKey: string; label: string; fixturesField: string; floorField: string }[] = [
  { categoryKey: 'Entry', label: 'Entry', fixturesField: 'EntryFixture', floorField: 'EntryFloorCovering' },
  { categoryKey: 'Hallway', label: 'Hallway', fixturesField: 'HallwayFixture', floorField: 'HallwayFloorCovering' },
  { categoryKey: 'Lounge', label: 'Lounge', fixturesField: 'LoungeFixtures', floorField: 'LoungeFloorCovering' },
  { categoryKey: 'Living', label: 'Living', fixturesField: 'LivingFixture', floorField: 'LivingFloorCovering' },
  { categoryKey: 'Media', label: 'Media', fixturesField: 'MediaFixture', floorField: 'MediaFloorCovering' },
  { categoryKey: 'Family', label: 'Family', fixturesField: 'FamilyFixture', floorField: 'FamilyFloorCovering' },
  { categoryKey: 'Dining', label: 'Dining', fixturesField: 'DiningFixture', floorField: 'DiningFloorCovering' },
  { categoryKey: 'Meals', label: 'Meals', fixturesField: 'MealsFixture', floorField: 'MealsFloorCovering' },
  { categoryKey: 'Study', label: 'Study', fixturesField: 'StudyFixture', floorField: 'StudyFloorCovering' },
  { categoryKey: 'Rumpus', label: 'Rumpus', fixturesField: 'RumpusFixture', floorField: 'RumpusFloorCovering' },
];

function kitchenTmp(selections: Record<string, string>, prefix: string): string {
  let tmp = '';
  const fixtures = selections[`${prefix}Fixtures`];
  if (fixtures) tmp += `equipped with a ${removeLastComma(fixtures)}  `;
  const cupboards = selections[`${prefix}Cupboards`];
  if (cupboards) tmp += `${cupboards} cabinets  `;
  const benchtops = selections[`${prefix}Benchtops`];
  if (benchtops) tmp += `${removeLastComma(benchtops)} benchtops  `;
  const floor = selections[`${prefix}FloorCovering`];
  if (floor) tmp += tmp.trim() !== '' ? `,${floor}  ` : `${floor}  `;
  return tmp.replace(/\s{2,}/g, ' ').trim();
}

function bathroomTmp(selections: Record<string, string>, prefix: string): string {
  let tmp = '';
  const fixtures = selections[`${prefix}Fixtures`];
  if (fixtures) tmp += `equipped with a ${removeLastComma(fixtures)} `;
  const floor = selections[`${prefix}FloorCoverings`] || selections[`${prefix}FloorCovering`];
  if (floor) tmp += tmp.trim() !== '' ? `,${floor}` : floor;
  return tmp.replace(/\s{2,}/g, ' ');
}

function bedroomTmp(selections: Record<string, string>, prefix: string): string {
  let tmp = '';
  const fixtures = selections[`${prefix}Fixtures`];
  if (fixtures) tmp += removeLastComma(fixtures);
  const floor = selections[`${prefix}FloorCovering`];
  if (floor) tmp += tmp.trim() !== '' ? `,${floor}` : floor;
  return tmp;
}

function findOption(group: string, value?: string): InternalDescriptionOption | undefined {
  if (!value) return undefined;
  return INTERNAL_DESCRIPTION_OPTIONS[group]?.find((option) => option.name.toLowerCase() === value.toLowerCase());
}

function indefiniteArticle(word: string): 'a' | 'an' {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

/** Builds e.g. "a detached single garage" from Configuration/No of Cars/Type, matching the GetDataForGarage-driven wording in tops-web-nz. */
function garagePhrase(typeValue: string, typeOption: InternalDescriptionOption | undefined, configValue: string | undefined, carsValue: string | undefined): string {
  const carsPart = carsValue ? `${carsValue.toLowerCase()} ` : '';
  if (typeOption?.parameter1 === '1' && configValue) {
    return `${indefiniteArticle(configValue)} ${configValue.toLowerCase()} ${carsPart}${typeValue.toLowerCase()}`;
  }
  return `${carsPart}${typeValue.toLowerCase()}`;
}

function garageTmp(selections: Record<string, string>, typeField: string, configField: string, carsField: string, fixturesField: string): string {
  const type = selections[typeField];
  if (!type) return '';
  const typeOption = findOption(typeField, type);
  let tmp = garagePhrase(type, typeOption, selections[configField], selections[carsField]);
  const fixtures = selections[fixturesField];
  if (fixtures) tmp += `, ${removeLastComma(fixtures)}`;
  return tmp;
}

export interface QuickChoiceCarAccommodation {
  carAccommodation: string;
  noOfUndercoverCarSpaces: number;
}

/** Port of the GetDataForGarage-driven CarAccomodation/NoOfUndercoverCarSpaces block inside CreateSentencePCItems. */
function garageCarAccommodation(selections: Record<string, string>, prefix: '' | 'Additional'): { text: string; cars: number } {
  const typeField = `${prefix}GarageType`;
  const typeValue = selections[typeField];
  if (!typeValue) return { text: '', cars: 0 };

  const configValue = selections[`${prefix}GarageConfiguration`];
  const carsValue = selections[`${prefix}GarageNoOfCars`];
  const carsOption = findOption(`${prefix}GarageNoOfCars`, carsValue);
  const cars = carsOption?.parameter1 ? parseInt(carsOption.parameter1, 10) : 0;

  const typeOption = findOption(typeField, typeValue);
  const text = garagePhrase(typeValue, typeOption, configValue, carsValue);

  return { text: prefix === 'Additional' ? `Additional ${text}` : text, cars: Number.isNaN(cars) || cars <= 0 ? 0 : cars };
}

/** Derives Car Accommodation/No of Undercover Car Spaces from the Garage selections, when Garage has been used. */
export function buildQuickChoiceCarAccommodation(selections: Record<string, string>): QuickChoiceCarAccommodation | null {
  if (!categoryHasValue(selections, 'Garage')) return null;

  const primary = garageCarAccommodation(selections, '');
  const additional = garageCarAccommodation(selections, 'Additional');
  const carAccommodation = [primary.text, additional.text]
    .filter(Boolean)
    .map((text) => toSentenceCase(text))
    .join(' ')
    .trim();

  return { carAccommodation, noOfUndercoverCarSpaces: primary.cars + additional.cars };
}

/** Port of CreateSentencePCItems: the per-room paragraph shown as "Accommodation Description" on the Finish tab. */
export function buildQuickChoicePcItems(selections: Record<string, string>, counts: QuickChoiceRoomCounts): string {
  const lines: string[] = [];

  for (const room of SIMPLE_ROOM_FIELDS) {
    if (!categoryHasValue(selections, room.categoryKey)) continue;
    lines.push(roomLine(room.label, combineFixturesAndFloor(selections, room.fixturesField, room.floorField)));
  }

  if (categoryHasValue(selections, 'Laundry')) {
    let tmp = '';
    const location = selections['LaundryLocation'];
    if (location) tmp += location;
    const fixture = selections['LaundryFixture'];
    if (fixture) tmp += tmp.trim() !== '' ? `, ${fixture}` : fixture;
    const floor = selections['LaundryFloor'];
    if (floor) tmp += tmp.trim() !== '' ? `,${floor}` : floor;
    lines.push(roomLine('Laundry', tmp));
  }

  if (categoryHasValue(selections, 'Kitchen')) lines.push(roomLine('Kitchen', kitchenTmp(selections, 'Kitchen')));
  if (categoryHasValue(selections, 'Kitchen2')) lines.push(roomLine('Kitchen 2', kitchenTmp(selections, 'Kitchen2')));

  let bathroomIndex = 0;
  for (const prefix of ['Bathroom1', 'Bathroom2', 'Bathroom3']) {
    if (!categoryHasValue(selections, prefix)) continue;
    bathroomIndex += 1;
    const label = counts.bathrooms === 1 ? 'Bathroom' : `Bathroom ${bathroomIndex}`;
    lines.push(roomLine(label, bathroomTmp(selections, prefix)));
  }

  if (categoryHasValue(selections, 'PowderRoom')) lines.push(roomLine('Powder Room', combineFixturesAndFloor(selections, 'PowderRoomFixtures', 'PowderRoomFloorCovering')));
  if (categoryHasValue(selections, 'PowderRoom2')) lines.push(roomLine('Powder Room 2', combineFixturesAndFloor(selections, 'PowderRoom2Fixtures', 'PowderRoom2FloorCovering')));
  if (categoryHasValue(selections, 'PowderRoom3')) lines.push(roomLine('Powder Room 3', combineFixturesAndFloor(selections, 'PowderRoom3Fixtures', 'PowderRoom3FloorCovering')));

  let bedroomIndex = 0;
  for (let i = 1; i <= 6; i++) {
    const prefix = `Bedroom${i}`;
    if (!categoryHasValue(selections, prefix)) continue;
    const tmp = bedroomTmp(selections, prefix);
    if (!tmp.trim()) continue;
    bedroomIndex += 1;
    const label = counts.bedrooms === 1 ? 'Bedroom' : `Bedroom ${bedroomIndex}`;
    lines.push(roomLine(label, tmp));
  }

  const ensuiteBedrooms = Array.from({ length: 6 }, (_, i) => i + 1).filter((i) => !!selections[`Bedroom${i}Ensuite`]);
  ensuiteBedrooms.forEach((bedroomNumber, index) => {
    const tmp = combineFixturesAndFloor(selections, `Bedroom${bedroomNumber}EnsuiteFixtures`, `Bedroom${bedroomNumber}EnsuiteFloorCovering`);
    const label = ensuiteBedrooms.length === 1 ? 'Ensuite' : `Ensuite ${index + 1}`;
    lines.push(roomLine(label, tmp));
  });

  if (categoryHasValue(selections, 'Garage')) {
    const primary = garageTmp(selections, 'GarageType', 'GarageConfiguration', 'GarageNoOfCars', 'GarageFixturesAndFittings');
    if (primary) lines.push(roomLine('Garage', primary));
    const additional = garageTmp(selections, 'AdditionalGarageType', 'AdditionalGarageConfiguration', 'AdditionalGarageNoOfCars', 'AdditionalGarageFixturesAndFittings');
    if (additional) lines.push(roomLine('Additional Garage', additional));
  }

  if (selections['OutdoorArea']) lines.push(`Outdoor Area : ${toSentenceCase(normalizeLastComma(removeLastComma(selections['OutdoorArea'])))}`);
  if (selections['MoreRooms']) lines.push(`More Rooms : ${toSentenceCase(normalizeLastComma(removeLastComma(selections['MoreRooms'])))}`);

  const separateToilets = Number(selections['Count'] || 0);
  if (separateToilets > 0) {
    lines.push(`Separate Toilets : ${toSentenceCase(`${unitsText(separateToilets).toLowerCase()} separate toilet${separateToilets > 1 ? 's' : ''}`)}`);
  }

  if (selections['Other']) lines.push(`Other: ${toSentenceCase(selections['Other'])}`);

  return lines.join('\n');
}

export interface QuickChoiceRoomCounts {
  bedrooms: number;
  bathrooms: number;
  livingAreas: number;
}

const LIVING_AREA_CATEGORIES = ['Lounge', 'Family', 'Rumpus', 'Living', 'Media'];
const BEDROOM_KEYS = ['Bedroom1', 'Bedroom2', 'Bedroom3', 'Bedroom4', 'Bedroom5', 'Bedroom6'];
const BATHROOM_KEYS = ['Bathroom1', 'Bathroom2', 'Bathroom3'];

/** Mirrors CreateSentences' NoOfBedrooms/NoOfBathrooms/numberofLivingAreas counters. */
export function countQuickChoiceRooms(selections: Record<string, string>): QuickChoiceRoomCounts {
  const bedrooms = BEDROOM_KEYS.filter((key) => categoryHasValue(selections, key)).length;
  const ensuiteCount = BEDROOM_KEYS.filter((key) => !!selections[`${key}Ensuite`]).length;
  const bathrooms = BATHROOM_KEYS.filter((key) => categoryHasValue(selections, key)).length + ensuiteCount;
  const livingAreas = LIVING_AREA_CATEGORIES.filter((key) => categoryHasValue(selections, key)).length;
  return { bedrooms, bathrooms, livingAreas };
}

export interface QuickChoiceFinishItem {
  noOfBedrooms: number;
  noOfBathrooms: number;
  numberOfLivingAreas: number;
  accommodationDescription: string;
  additionalFeatures: string;
  chattels: string;
  noOfUndercoverCarSpaces: number;
  carAccommodation: string;
}

/** Recomputes the auto-generated fields; noOfUndercoverCarSpaces/carAccommodation carry over untouched unless Garage selections drive them, matching the legacy tab. */
export function buildQuickChoiceFinishItem(
  selections: Record<string, string>,
  previous: Pick<QuickChoiceFinishItem, 'noOfUndercoverCarSpaces' | 'carAccommodation'>,
): QuickChoiceFinishItem {
  const sentences = buildQuickChoiceSentences(selections);
  const counts = countQuickChoiceRooms(selections);
  const carAccommodation = buildQuickChoiceCarAccommodation(selections);
  return {
    noOfBedrooms: counts.bedrooms,
    noOfBathrooms: counts.bathrooms,
    numberOfLivingAreas: counts.livingAreas,
    accommodationDescription: buildQuickChoicePcItems(selections, counts),
    additionalFeatures: sentences.fixturesAndFeatures,
    chattels: sentences.chattels,
    noOfUndercoverCarSpaces: carAccommodation?.noOfUndercoverCarSpaces ?? previous.noOfUndercoverCarSpaces,
    carAccommodation: carAccommodation?.carAccommodation ?? previous.carAccommodation,
  };
}
